import type { Meeting } from "@repo/types";
import { MeetingThumbnail } from "./meetingThumbnail";
import { useTenantStore } from "@/stores/tenantStore";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { DatePicker } from "@repo/ui/components/Base/DatePicker";
import { TablePagination } from "./TablePagination";
import {
  defaultDateReviver,
  useLocalStorageValue,
} from "@/hooks/useLocalStorageValue";
import { FilterX } from "lucide-react";
import { useUserStore } from "@/stores/userStore";
import { meetingRepo } from "@/repositories/meetingRepo";
import { firstValueFrom } from "rxjs";

export type MeetingsPreviewConfig = {
  pageSize: number;
  minDate?: Date;
  maxDate?: Date;
};

export const MeetingsTiledView = () => {
  const navigate = useNavigate();
  const tenantId = useTenantStore((s) => s.id);
  const team = useUserStore((s) => s.activeTeam);
  const teamId = typeof team === "object" ? team.id : undefined;
  const tenantTimeZone = useTenantStore((s) => s.tenant?.timeZone);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [recordsCount, setRecordsCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const defaultConfig: MeetingsPreviewConfig = {
    pageSize: 10,
    minDate: undefined,
    maxDate: undefined,
  };

  const configKey = `civi-docs-meetingsPreviewConfig_${tenantId}`;

  const config = useLocalStorageValue<MeetingsPreviewConfig>(
    configKey,
    defaultConfig,
    undefined,
    { reviver: defaultDateReviver }
  );

  useEffect(() => {
    let cancel = false;

    (async () => {
      if (!tenantId) return;
      const allMeetings = await firstValueFrom(meetingRepo.list(tenantId));
      if (cancel) return;

      let filteredMeetings = allMeetings;
      // filter by team
      if (teamId) {
        filteredMeetings = filteredMeetings.filter((m) => m.teamId === teamId);
      }
      // filter by start time
      filteredMeetings = filteredMeetings.filter(
        (m) => m.startTime >= (config.value.minDate ?? new Date())
      );
      // filter by end time
      if (config.value.maxDate) {
        filteredMeetings = filteredMeetings.filter(
          (m) => m.startTime <= config.value.maxDate!
        );
      }
      // sort by start time
      filteredMeetings = filteredMeetings.sort(
        (a, b) => a.startTime.getTime() - b.startTime.getTime()
      );
      // update state
      setRecordsCount(filteredMeetings.length);
      setMeetings(
        filteredMeetings.slice(
          page * config.value.pageSize,
          page * config.value.pageSize + config.value.pageSize
        )
      );
    })();

    return () => {
      cancel = true;
    };
  }, [
    tenantId,
    teamId,
    config.value.minDate,
    config.value.maxDate,
    config.value.pageSize,
    page,
  ]);
  const handleEdit = (id: string) => {
    navigate(new AppUrlPath(tenantId).getMeetingPath(`${id}`) + "#edit=true");
  };

  return (
    <div className="@container">
      <div className="flex flex-row gap-4 content-center">
        <DatePicker
          className=" h-6 text-xs"
          date={config.value.minDate ?? new Date()}
          id="date"
          onDateChange={(date) =>
            config.setValue((prev) => ({ ...prev, minDate: date }))
          }
        />
        <p className="mt-1.5">to</p>
        <DatePicker
          className=" h-6 text-xs"
          date={config.value.maxDate}
          id="to"
          onDateChange={(date) =>
            config.setValue((prev) => ({ ...prev, maxDate: date }))
          }
        />
        <button
          onClick={() => {
            config.setValue((prev) => ({
              ...prev,
              minDate: undefined,
              maxDate: undefined,
            }));
          }}
        >
          <FilterX className="h-4 w-4" />
        </button>
      </div>
      <div
        className={
          "grid gap-4 pt-4 justify-center " +
          "[grid-template-columns:repeat(auto-fit,minmax(208px,208px))]"
        }
      >
        {meetings.map((m) => (
          <MeetingThumbnail
            key={m.id}
            meeting={m}
            onClick={handleEdit}
            timeZone={tenantTimeZone}
          />
        ))}
      </div>
      <div>
        <TablePagination
          onPageChange={setPage}
          onPageSizeChange={(size) =>
            config.setValue((prev) => ({ ...prev, pageSize: size }))
          }
          pageIndex={page}
          pageSize={config.value.pageSize}
          totalRows={recordsCount}
        />
      </div>
    </div>
  );
};
